import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { KeyRound, Loader2, LogOut, MonitorSmartphone, Save, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { updateProfileSchema, type UpdateProfileInput } from '@fenix/shared';
import { PageHeader } from '@/components/layout/PageHeader';
import { RoleBadge } from '@/components/RoleBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { authApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { fmtDateTime, fmtRelative } from '@/lib/format';
import { describeUserAgent } from '@/lib/userAgent';
import { PasswordFields, type PasswordFormValues } from '@/features/auth/PasswordFields';
import { EMPTY_PASSWORD_FORM, passwordFormSchema } from '@/features/auth/passwordForm';
import { useAuthStore } from '@/features/auth/store';

const errMsg = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback);

export function AccountPage() {
  const qc = useQueryClient();
  const { user, setUser } = useAuthStore();

  const profile = useForm<UpdateProfileInput>({
    resolver: zodResolver(updateProfileSchema),
    defaultValues: { name: user?.name ?? '' },
  });
  useEffect(() => {
    profile.reset({ name: user?.name ?? '' });
  }, [user?.name, profile]);

  const saveProfile = useMutation({
    mutationFn: authApi.updateProfile,
    onSuccess: (u) => {
      setUser(u);
      toast.success('Perfil atualizado');
    },
    onError: (e) => toast.error(errMsg(e, 'Erro ao salvar')),
  });

  const password = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: EMPTY_PASSWORD_FORM,
  });
  const changePassword = useMutation({
    mutationFn: (v: PasswordFormValues) =>
      authApi.changePassword({ currentPassword: v.currentPassword, newPassword: v.newPassword }),
    onSuccess: (u) => {
      setUser(u);
      password.reset(EMPTY_PASSWORD_FORM);
      toast.success('Senha alterada. As outras sessões foram encerradas.');
      void qc.invalidateQueries({ queryKey: ['sessions'] });
    },
    onError: (e) => toast.error(errMsg(e, 'Erro ao trocar a senha')),
  });

  const sessions = useQuery({ queryKey: ['sessions'], queryFn: authApi.sessions });
  const revokeOne = useMutation({
    mutationFn: authApi.revokeSession,
    onSuccess: () => {
      toast.success('Sessão encerrada');
      void qc.invalidateQueries({ queryKey: ['sessions'] });
    },
    onError: (e) => toast.error(errMsg(e, 'Erro')),
  });
  const revokeOthers = useMutation({
    mutationFn: authApi.revokeOtherSessions,
    onSuccess: ({ revoked }) => {
      toast.success(revoked ? `${revoked} sessão(ões) encerrada(s)` : 'Nenhuma outra sessão ativa');
      void qc.invalidateQueries({ queryKey: ['sessions'] });
    },
    onError: (e) => toast.error(errMsg(e, 'Erro')),
  });

  if (!user) return null;
  const others = sessions.data?.filter((s) => !s.current).length ?? 0;

  return (
    <div>
      <PageHeader title="Minha conta" description="Seus dados, senha e dispositivos conectados" />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <UserRound className="size-4" /> Perfil
            </CardTitle>
            <CardDescription>
              O e-mail e o perfil de acesso só um administrador altera.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={profile.handleSubmit((v) => saveProfile.mutate(v))}
              className="space-y-4"
              noValidate
            >
              <div className="space-y-2">
                <Label htmlFor="account-name">Nome</Label>
                <Input id="account-name" {...profile.register('name')} />
                {profile.formState.errors.name && (
                  <p className="text-xs text-destructive">
                    {profile.formState.errors.name.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>E-mail</Label>
                <Input value={user.email} disabled />
              </div>
              <div className="flex items-center gap-2">
                <Label>Perfil</Label>
                <RoleBadge role={user.role} />
              </div>
              <Button type="submit" disabled={saveProfile.isPending || !profile.formState.isDirty}>
                {saveProfile.isPending ? <Loader2 className="animate-spin" /> : <Save />} Salvar
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <KeyRound className="size-4" /> Senha
            </CardTitle>
            <CardDescription>
              Ao trocar, todas as outras sessões são encerradas; esta continua.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={password.handleSubmit((v) => changePassword.mutate(v))}
              className="space-y-4"
              noValidate
            >
              <PasswordFields form={password} idPrefix="account" />
              <Button type="submit" disabled={changePassword.isPending}>
                {changePassword.isPending && <Loader2 className="animate-spin" />} Alterar senha
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <MonitorSmartphone className="size-4" /> Sessões ativas
              </CardTitle>
              <CardDescription>
                Dispositivos conectados com a sua conta. Encerre os que não reconhecer.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={!others || revokeOthers.isPending}
              onClick={() => revokeOthers.mutate()}
            >
              <LogOut /> Sair dos outros dispositivos
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {sessions.isLoading ? (
              <div className="space-y-2 p-4">
                {Array.from({ length: 2 }).map((_, i) => (
                  <Skeleton key={i} className="h-10" />
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Dispositivo</TableHead>
                    <TableHead>IP</TableHead>
                    <TableHead>Iniciada</TableHead>
                    <TableHead>Última atividade</TableHead>
                    <TableHead className="w-24" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.data?.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span title={s.userAgent ?? undefined}>
                            {describeUserAgent(s.userAgent)}
                          </span>
                          {s.current && <Badge variant="secondary">Esta sessão</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{s.ip ?? '—'}</TableCell>
                      <TableCell className="text-sm">{fmtDateTime(s.createdAt)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {fmtRelative(s.lastUsedAt ?? s.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        {!s.current && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={revokeOne.isPending}
                            onClick={() => revokeOne.mutate(s.id)}
                          >
                            Encerrar
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
